import { useQuery } from "@tanstack/react-query";
import { api } from "@/core/api/client";

export interface Capabilities {
  /** Team play: the Player shows teammates' positions (ADR 0028). */
  presence: boolean;
  autosave: boolean;
  live_feedback: boolean;
  printable: boolean;
  multi_attempt: boolean;
  preview_in_editor: boolean;
  /** The verdict carries points and the leaderboard ranks on them first (ADR 0040). */
  scored: boolean;
  /**
   * A wrong answer's feedback is graded information the player acts on: no cooldown, and
   * the Player owns the feedback through `applyVerdict` (ADR 0041).
   */
  graded_feedback: boolean;
}

/** Optional attribution for a puzzle taken from elsewhere. Either half may be empty. */
export interface PuzzleCredit {
  author: string;
  url: string;
}

export interface PuzzleSummary {
  slug: string;
  title: string;
  author: string;
  difficulty: number;
  status: string;
  tags: string[];
  puzzle_type: string;
  published_at: string | null;
  capabilities: Capabilities;
}

export interface PuzzleVersionView {
  id: number;
  number: number;
  definition: unknown;
  type_version: number;
  is_frozen: boolean;
  has_solution: boolean;
  created_at: string;
}

interface Page<T> {
  results: T[];
  count: number;
}

/**
 * Published puzzles. `archived` lists the ones that left the current rotation; they stay
 * playable and rankable forever, so this is a filter, not a closing (CLAUDE.md §11).
 */
export function usePublishedPuzzles(status?: "published" | "archived") {
  return useQuery({
    queryKey: ["puzzles", "published", status ?? "all"],
    queryFn: () => api<Page<PuzzleSummary>>(`/api/puzzles/${status ? `?status=${status}` : ""}`),
  });
}

export function usePuzzle(slug: string) {
  return useQuery({
    queryKey: ["puzzles", slug],
    queryFn: () => api<PuzzleSummary>(`/api/puzzles/${slug}/`),
  });
}

export function usePuzzlePreview(slug: string, enabled: boolean) {
  return useQuery({
    queryKey: ["puzzles", slug, "preview"],
    queryFn: () =>
      api<{
        puzzle: PuzzleSummary;
        version: PuzzleVersionView | null;
        credit: PuzzleCredit | null;
      }>(`/api/puzzles/${slug}/preview/`),
    enabled,
  });
}

/** One line of the recent-solves feed: a leaderboard row plus the puzzle it is on. */
export interface RecentSolve {
  puzzle: { slug: string; title: string; puzzle_type: string };
  participant: string;
  is_team: boolean;
  is_me: boolean;
  /** The participant's rank on that puzzle as it stands now, not at the time of the solve. */
  rank: number;
  elapsed_ms: number;
  score: number | null;
  /** Whether `score` means anything: the type ranks on points (ADR 0040). */
  scored: boolean;
  submitted_at: string;
}

/**
 * The last solves across the tenant, newest first (ADR 0043). Pushed over the socket as
 * `activity.changed`; while it is down, the same poll as a leaderboard: 15 s, 60 s hidden.
 */
export function useRecentSolves({ live = false }: { live?: boolean } = {}) {
  return useQuery({
    queryKey: ["activity"],
    queryFn: () => api<{ solves: RecentSolve[] }>("/api/activity/"),
    refetchInterval: () => {
      if (live) return false;
      return typeof document !== "undefined" && document.hidden ? 60_000 : 15_000;
    },
    refetchIntervalInBackground: true,
  });
}
